import type { SolicitudDetalle } from "@/lib/types";

/**
 * Estados pre-autorización · usados para polling agresivo del detalle
 * porque el agregado puede transitar por outbox/dispatcher en estos
 * estados (validación SAT, derivación de organización, etc.).
 */
export const ESTADOS_PENDIENTES_POLLING = new Set([
  "Iniciada",
  "TipoSeleccionado",
  "SubtipoSeleccionado",
  "CfdiPendiente",
  "CfdiCargado",
  "EnValidacionReglas",
  "EnValidacionFiscal",
  "Autorizada",
]);

/**
 * Estados desde los que el solicitante puede cancelar su propia solicitud.
 *
 * Lista alineada con `SolicitudDePago.CancelarPorSolicitante` del backend:
 * todos los estados PRE-autorización, incluyendo los rechazados (Reglas/Fiscal/
 * Suspendida) donde el usuario aún tiene la solicitud viva pero puede preferir
 * abortarla en vez de reintentar la carga del CFDI. Incluye
 * `ContratoAnualizadoSeleccionado`/`OrdenDeCompraSeleccionada` (propios de los
 * tipos CA/OC, entre elegir el subtipo y cargar el CFDI): esta lista se quedó
 * desactualizada cuando se agregaron esos tipos y el botón "Cancelar
 * solicitud" quedaba oculto justo en ese paso, aunque el backend ya lo
 * permitía. Cancelar ahí libera automáticamente el consumo/saldo del
 * contrato u orden asociada (`EstadosDeConsumo.NoConsumen`, compartido) y el
 * UUID del CFDI si ya se había cargado uno (`CfdiUuidPortAdapter`).
 *
 * Estados que NO permiten cancelar (el backend pide pedir baja a Mesa de Control):
 *   ListoParaAutorizacion, EnAutorizacionNivel1/N, RechazadaParaCorreccion,
 *   Autorizada, EnviadaAContabilizacion, PolizaGenerada, EnColaContabilizacion,
 *   DescartadaPorPlazoVencido y los terminales (DadaDeBajaPorAutorizador,
 *   CanceladaPorSolicitante).
 */
export const ESTADOS_CANCELABLES_POR_SOLICITANTE = new Set([
  "Iniciada",
  "TipoSeleccionado",
  "SubtipoSeleccionado",
  "ContratoAnualizadoSeleccionado",
  "OrdenDeCompraSeleccionada",
  "CfdiPendiente",
  "CfdiCargado",
  "EnValidacionReglas",
  "ReglasRechazadas",
  "EnValidacionFiscal",
  "Validado",
  "RechazadoFiscal",
  "SuspendidaFiscal",
  "CapturandoDatos",
  "SeleccionandoProveedor",
  "Prorrateando",
  "ProvisionalmenteListo",
  "AdjuntandoDocumentos",
]);

/**
 * Estados desde los que Mesa de Control puede dar de baja una solicitud
 * (decisión P29) · espejo exacto de la guarda en
 * `SolicitudDePago.DarDeBajaPorMesaDeControl` del backend. A diferencia de
 * `ESTADOS_CANCELABLES_POR_SOLICITANTE`, esto SÍ incluye estados posteriores
 * a la autorización — incluida `Autorizada` (con o sin póliza ya generada).
 * `EnColaContabilizacion`/`PolizaGenerada`/`EnviadaAContabilizacion` se listan
 * por completitud (hoy inalcanzables como `Estado` · la contabilización vive
 * en la property booleana `enviadaAContabilizacion`, que es lo que el modal
 * de baja usa para mostrar la advertencia de póliza ya depositada).
 */
export const ESTADOS_BAJA_POR_MESA_CONTROL = new Set([
  "ListoParaAutorizacion",
  "EnAutorizacionNivel1",
  "EnAutorizacionNivelN",
  "RechazadaParaCorreccion",
  "Autorizada",
  "EnColaContabilizacion",
  "PolizaGenerada",
  "EnviadaAContabilizacion",
]);

/** Estados que indican que la solicitud está en el flujo de autorización o lo terminó. */
export function estaEnFlujoAutorizacion(estado: string): boolean {
  return (
    estado === "EnAutorizacionNivel1" ||
    estado === "EnAutorizacionNivelN" ||
    estado === "RechazadaParaCorreccion" ||
    estado === "Autorizada" ||
    estado === "EnviadaAContabilizacion" ||
    estado === "DadaDeBajaPorAutorizador"
  );
}

/**
 * Estados en los que la pantalla debe enfocarse SOLO en el flujo de
 * autorización (flujo + chat) · sin tabs distractoras. Una vez autorizada
 * o enviada a contabilización volvemos al layout de tabs.
 */
export function estaEnEsperaDeAutorizacion(estado: string): boolean {
  return (
    estado === "EnAutorizacionNivel1" ||
    estado === "EnAutorizacionNivelN" ||
    estado === "RechazadaParaCorreccion"
  );
}

/**
 * Estados terminales · la solicitud ya no avanza ni admite acciones del
 * solicitante (cancelada/dada de baja/descartada, o ya autorizada/contabilizada).
 * En estos estados NO tiene sentido invitar a cargar CFDI ni continuar el wizard.
 */
export const ESTADOS_TERMINALES = new Set([
  "CanceladaPorSolicitante",
  "DadaDeBajaPorAutorizador",
  "DescartadaPorPlazoVencido",
  "Autorizada",
  "EnColaContabilizacion",
  "PolizaGenerada",
  "EnviadaAContabilizacion",
]);

export function esEstadoTerminal(estado: string): boolean {
  return ESTADOS_TERMINALES.has(estado);
}

/** Estados que muestran el card "Reintentar carga del CFDI" en la sidebar. */
export function puedeReintentarCarga(estado: string): boolean {
  return (
    estado === "ReglasRechazadas" ||
    estado === "RechazadoFiscal" ||
    estado === "SuspendidaFiscal"
  );
}

/**
 * Calcula el intervalo de polling apropiado según el estado de la
 * solicitud. Estados pendientes (transitando vía outbox) usan 2s ·
 * estados estables apagan el polling.
 */
export function calcularIntervaloPolling(sol: SolicitudDetalle | undefined): number | false {
  if (!sol) return false;
  const pendiente = ESTADOS_PENDIENTES_POLLING.has(sol.estado);
  const esperandoCont =
    (sol.estado === "ListoParaAutorizacion" ||
      sol.estado === "EnAutorizacionNivel1" ||
      sol.estado === "EnAutorizacionNivelN") &&
    !sol.enviadaAContabilizacion;
  return pendiente || esperandoCont ? 2_000 : false;
}

/**
 * Decide el tab inicial al abrir el detalle:
 *   · contabilización si la póliza ya existe (la novedad relevante)
 *   · autorización si la solicitud fue rechazada (acción pendiente)
 *   · resumen como default
 */
export function calcularTabInicial(sol: SolicitudDetalle): string {
  if (sol.enviadaAContabilizacion) return "contabilizacion";
  if (sol.estado === "RechazadaParaCorreccion") return "autorizacion";
  return "resumen";
}
